import { classes, stylesheet } from 'typestyle';
import React, { useEffect, useState } from 'react';
import L from 'leaflet';
import { CloseIcon, FullscreenIcon } from '@traceair/webapp-icons';
import { BlackOpacity10, Gray170 } from '@traceair/tds';
import { trackAction } from '../../../../../libs/analytics/ActionTracking';

export const expandMapStyles = stylesheet({
  button: {
    display: 'flex',
    width: 24,
    height: 24,
    cursor: 'pointer',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    top: 8,
    right: 8,
    borderRadius: 8,
    background: BlackOpacity10,
    backdropFilter: 'blur(12.5px)',
    zIndex: 2,
    $nest: {
      '&:hover': {
        opacity: 0.85,
      },
      '&:active': {
        opacity: 0.75,
      },
    },
  },
  buttonExpand: {
    display: 'none',
  },
  buttonClose: {
    $nest: {
      '&&': {
        width: 32,
        height: 32,
        borderRadius: 10,
      },
    },
  },
  expandedContainer: {
    $nest: {
      '&&': {
        $nest: {
          '&&': {
            cursor: 'auto',
            position: 'fixed',
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            margin: 'auto',
            width: '100%',
            height: '100%',
            zIndex: 9999,
            borderRadius: 0,
            border: 'none',
          },
        },
      },
    },
  },
});

const closeOnEscape = (expanded: boolean, setExpanded: React.Dispatch<React.SetStateAction<boolean>>) => {
  useEffect(() => {
    if (expanded) {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          trackAction('Collapse mission map with Esc', null);
          setExpanded(false);
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [expanded]);
};

const redrawMapOnToggle = (
  expanded: boolean,
  mapInstanceRef: React.MutableRefObject<L.Map | null>,
  boundaryLayerRef: React.MutableRefObject<L.Polygon | null>,
) => {
  useEffect(() => {
    mapInstanceRef.current?.invalidateSize({ animate: false });

    const bounds = boundaryLayerRef.current?.getBounds();
    if (bounds) {
      mapInstanceRef.current?.fitBounds(bounds, { animate: false });
    }
  }, [expanded]);
};

export const useExpandMap = (
  mapInstanceRef: React.MutableRefObject<L.Map | null>,
  boundaryLayerRef: React.MutableRefObject<L.Polygon | null>,
) => {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    mapInstanceRef.current?.invalidateSize({ animate: false });

    const bounds = boundaryLayerRef.current?.getBounds();
    if (bounds) {
      mapInstanceRef.current?.fitBounds(bounds, { animate: false });
    }
  }, [expanded]);

  const handleExpandMap = (event: React.MouseEvent) => {
    event.stopPropagation();
    setExpanded(true);
    trackAction('Expand mission map', null);
  };

  const handleCollapseMap = (event: React.MouseEvent) => {
    event.stopPropagation();
    setExpanded(false);
    trackAction('Collapse mission map', null);
  };

  closeOnEscape(expanded, setExpanded);
  redrawMapOnToggle(expanded, mapInstanceRef, boundaryLayerRef);

  const toggleExpandButton = expanded ? (
    <button className={classes(expandMapStyles.button, expandMapStyles.buttonClose)} onClick={handleCollapseMap}>
      <CloseIcon size={20} color={Gray170} />
    </button>
  ) : (
    <button
      className={classes(expandMapStyles.button, expandMapStyles.buttonExpand, 'mission-map-expand')}
      onClick={handleExpandMap}
    >
      <FullscreenIcon size={16} color={Gray170} />
    </button>
  );

  return {
    toggleExpandButton,
    expanded,
  };
};
